import { type ReactNode } from "react";
import { cn } from "@/shared/utils/cn";

type Tone =
  | "neutral"
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "super"
  | "outline"
  | "indigo"
  | "purple"
  | "pink"
  | "rose"
  | "amber"
  | "orange"
  | "teal"
  | "cyan"
  | "lime"
  | "emerald"
  | "sky"
  | "violet"
  | "fuchsia"
  | "slate"
  | "stone";

export interface ChipProps {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}

const toneClasses: Record<Tone, string> = {
  neutral: "bg-[var(--gray-150)] text-[var(--gray-800)] ring-1 ring-inset ring-[var(--gray-300)]",
  info: "bg-[var(--accent-100)] text-[var(--accent-800)] ring-1 ring-inset ring-[var(--accent-300)]",
  success: "bg-[var(--success-100)] text-[var(--success-800)] ring-1 ring-inset ring-[var(--success-300)]",
  warning: "bg-[var(--warning-100)] text-[var(--warning-800)] ring-1 ring-inset ring-[var(--warning-300)]",
  danger: "bg-[var(--danger-100)] text-[var(--danger-800)] ring-1 ring-inset ring-[var(--danger-300)]",
  super: "bg-[var(--super-100)] text-[var(--super-800)] ring-1 ring-inset ring-[var(--super-300)]",
  outline: "bg-white text-[var(--gray-700)] ring-1 ring-inset ring-[var(--gray-300)]",
  indigo: "bg-[var(--chip-indigo-bg)] text-[var(--chip-indigo-fg)] ring-1 ring-inset ring-[var(--chip-indigo-ring)]",
  purple: "bg-[var(--chip-purple-bg)] text-[var(--chip-purple-fg)] ring-1 ring-inset ring-[var(--chip-purple-ring)]",
  pink: "bg-[var(--chip-pink-bg)] text-[var(--chip-pink-fg)] ring-1 ring-inset ring-[var(--chip-pink-ring)]",
  rose: "bg-[var(--chip-rose-bg)] text-[var(--chip-rose-fg)] ring-1 ring-inset ring-[var(--chip-rose-ring)]",
  amber: "bg-[var(--chip-amber-bg)] text-[var(--chip-amber-fg)] ring-1 ring-inset ring-[var(--chip-amber-ring)]",
  orange: "bg-[var(--chip-orange-bg)] text-[var(--chip-orange-fg)] ring-1 ring-inset ring-[var(--chip-orange-ring)]",
  teal: "bg-[var(--chip-teal-bg)] text-[var(--chip-teal-fg)] ring-1 ring-inset ring-[var(--chip-teal-ring)]",
  cyan: "bg-[var(--chip-cyan-bg)] text-[var(--chip-cyan-fg)] ring-1 ring-inset ring-[var(--chip-cyan-ring)]",
  lime: "bg-[var(--chip-lime-bg)] text-[var(--chip-lime-fg)] ring-1 ring-inset ring-[var(--chip-lime-ring)]",
  emerald: "bg-[var(--chip-emerald-bg)] text-[var(--chip-emerald-fg)] ring-1 ring-inset ring-[var(--chip-emerald-ring)]",
  sky: "bg-[var(--chip-sky-bg)] text-[var(--chip-sky-fg)] ring-1 ring-inset ring-[var(--chip-sky-ring)]",
  violet: "bg-[var(--chip-violet-bg)] text-[var(--chip-violet-fg)] ring-1 ring-inset ring-[var(--chip-violet-ring)]",
  fuchsia: "bg-[var(--chip-fuchsia-bg)] text-[var(--chip-fuchsia-fg)] ring-1 ring-inset ring-[var(--chip-fuchsia-ring)]",
  slate: "bg-[var(--chip-slate-bg)] text-[var(--chip-slate-fg)] ring-1 ring-inset ring-[var(--chip-slate-ring)]",
  stone: "bg-[var(--chip-stone-bg)] text-[var(--chip-stone-fg)] ring-1 ring-inset ring-[var(--chip-stone-ring)]",
};

export function Chip({ tone = "neutral", children, className }: ChipProps) {
  return (
    <span
      className={cn(
        // Sizing — fixed height, hugs content, never shrinks below it in flex parents.
        "inline-flex h-[22px] min-h-[22px] w-fit max-w-full shrink-0 grow-0 items-center justify-center",
        // Spacing + radius
        "gap-1 rounded-full px-2 box-border align-middle",
        // Typography
        "text-[11.5px] font-semibold leading-none tracking-[0.01em] whitespace-nowrap",
        // Overflow — long values truncate with ellipsis instead of breaking row width.
        "overflow-hidden text-ellipsis",
        toneClasses[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
